<template>
  <FilterSelection
    :disabled="disabled"
    :get-item-id="getEventId"
    :get-item-string-compare="getEventName"
    v-model="internalValue"
  >
    <template #selection="{ item }">
      <EventCell v-bind="item" variant="row" />
    </template>
    <AppAutosuggest
      :disabled="disabled"
      :input-props="inputProps"
      :section-configs="sectionConfigs"
      :suggestions="sectionedSuggestions"
      :value="focused ? search : placeholder"
      @input="onInput"
      @selected="onSelect"
      @focus="onFocus"
      @blur="onBlur"
    >
      <template #before-suggestions>
        <Loading v-if="loading" />
        <ErrorAlert v-else-if="error">
          {{ error }}
        </ErrorAlert>
        <InfoAlert v-else-if="!suggestions.length && search.length > 0 && search.length < 2">
          Enter at least 2 characters to get results
        </InfoAlert>
        <InfoAlert v-else-if="!suggestions.length"> No matching events </InfoAlert>
      </template>
      <template #default="{ suggestion }">
        <DropdownItem :disabled="disabled" :selected="selectedEventIds[suggestion.item.id]">
          <EventCell v-bind="suggestion.item" variant="row" />
        </DropdownItem>
      </template>
    </AppAutosuggest>
  </FilterSelection>
</template>

<script>
import * as Sentry from '@sentry/vue'
import px from 'vue-types'

import { getTournamentsAutocomplete } from '@/api/grid.js'
import axios from '@/axios.js'
import AppAutosuggest from '@/components/generic/Autosuggest.vue'
import FilterSelection from '@/components/Search/filters/FilterSelection.vue'
import EventCell from '@/components/Table/cells/EventCell.vue'

import DropdownItem from '../../../Form/DropdownItem.vue'
import ErrorAlert from '../../../generic/ErrorAlert.vue'
import InfoAlert from '../../../generic/InfoAlert.vue'
import Loading from '../../../generic/Loading.vue'

export default {
  name: 'GridEventFilter',
  components: {
    AppAutosuggest,
    DropdownItem,
    ErrorAlert,
    FilterSelection,
    InfoAlert,
    Loading,
    EventCell,
  },
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    disabled: px.bool.def(false),
    value: px.arrayOf(px.object),
    excludeEvents: px.arrayOf(px.string).def([]),
    apiKey: px.string.def(''),
  },
  data() {
    return {
      error: null,
      focused: false,
      loading: 0,
      search: '',
      suggestions: [],
    }
  },
  computed: {
    inputProps() {
      return {
        class: 'form-control',
        id: 'event-search-filter',
        placeholder: this.placeholder,
      }
    },

    internalValue: {
      get: function () {
        return this.value || []
      },
      set: function (value) {
        return this.$emit('update', value)
      },
    },

    placeholder() {
      switch (this.internalValue.length) {
        case 0:
          return 'Any event'
        case 1:
          return '1 event selected'
        default:
          return `${this.internalValue.length} events selected`
      }
    },

    sectionConfigs() {
      return {
        default: {},
        empty: { type: 'div' },
        error: { type: 'div' },
        loading: { type: 'div' },
      }
    },

    sectionedSuggestions() {
      if (this.loading) return [{ name: 'loading', data: [{ loading: true }] }]
      if (this.error) return [{ name: 'error', data: [{ error: this.error }] }]
      if (!this.suggestions.length) return [{ name: 'empty', data: [{ empty: true }] }]
      return [{ data: this.suggestions }]
    },

    selectedEventIds() {
      return Object.freeze(Object.fromEntries(this.internalValue.map(event => [this.getEventId(event), true])))
    },
  },
  beforeDestroy() {
    this.cancelRequest()
  },
  methods: {
    cancelRequest() {
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel()
        this.cancelTokenSource = null
      }
    },
    getEventId: event => event.id,
    getEventName: event => event.name,
    async onInput(search = '') {
      this.search = search
      this.cancelRequest()
      this.suggestions = []
      this.error = null
      if (search.length > 0 && search.length < 2) return
      this.cancelTokenSource = axios.CancelToken.source()
      this.loading++
      try {
        if (search.length === 0) {
          if (this.recentSuggestions != null) {
            this.suggestions = this.recentSuggestions
            return
          }
        }

        const data =
          search.length > 0
            ? await getTournamentsAutocomplete(
                { search },
                {
                  apiKey: this.apiKey,
                  cancelToken: this.cancelTokenSource.token,
                }
              )
            : []

        this.suggestions = Object.freeze(
          (data?.data?.tournaments?.edges || []).map(edge => {
            return Object.freeze({
              id: edge.node.id,
              name: edge.node.name,
              logo_url: edge.node.logoUrl,
            })
          })
        )

        if (search.length === 0) {
          this.recentSuggestions = this.suggestions
        }
      } catch (e) {
        if (axios.isCancel(e)) {
          return
        }
        this.error = axios.extractErrorMessage(e)
        console.error(this.error, e)
        Sentry.captureException(e)
      } finally {
        this.loading--
      }
    },
    onSelect(selection) {
      if (!selection) return
      const { item: event } = selection
      const eventId = this.getEventId(event)
      this.internalValue = this.selectedEventIds[eventId]
        ? // remove
          this.internalValue.filter(event => this.getEventId(event) !== eventId)
        : // add
          [...this.internalValue, event]
      this.search = ''
      this.suggestions = []
    },
    onFocus() {
      this.focused = true
      this.suggestions = this.recentSuggestions || []
      if (!this.recentSuggestions) {
        this.onInput('')
      }
    },
    onBlur() {
      this.focused = false
      this.search = ''
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .alert {
    margin: 0;
    border-radius: 0;
  }
}
</style>
